// src/domains/withdraw.ts —— 06 A-07 提佣（余额卡 / 申请表单 / 6 态记录表；切片 s.wd）
import { ApiError } from '../../../shared/src/api/client';
import type { AdminPage, WithdrawQuote } from '../../../shared/src/api/types';
import { interpolate } from '../../../shared/src/errors';
import { cmp, fmtAmount, fmtCommission, fmtPlain, parseDecimalInput, sub, toMicro, type Micro } from '../../../shared/src/money';
import { fmtDateTime } from '../../../shared/src/time';
import { I18N_WITHDRAW, REWRITES_WITHDRAW } from '../i18n/withdraw';
import type { AgentState } from '../state';
import type { BoundUser, WithdrawalItem } from '../types';
import { chipStyle, type ChipKind } from '../ui';
import { homeOf, loadOverview } from './home';
import { str, type AgentCtx, type AgentDomain, type InputEvent } from './types';
import { KEYS, MODAL_KEYS } from './withdraw.keys';

// 06 A-07：TRON 地址 Base58（与后端 chain.CanonicalAddress 前置格式一致）
const TRON_RE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
const LIST_SIZE = 50;

export type WdMethod = WithdrawalItem['method'];

export interface WithdrawSlice {
  method: WdMethod;
  amount: string;
  addr: string;
  list: readonly WithdrawalItem[];
  /** 2026-10-15：USDT 提佣预检结果；key = 金额|地址，与当前输入不一致即视为过期、不展示 */
  quote: { key: string; q: WithdrawQuote } | null;
}

declare module '../state' {
  interface AgentSlices {
    wd?: WithdrawSlice;
  }
}

function fresh(): WithdrawSlice {
  return { method: 'usdt', amount: '', addr: '', list: [], quote: null };
}

const WD0: WithdrawSlice = fresh();

export function wdOf(s: AgentState): WithdrawSlice {
  return s.wd ?? WD0;
}

function setWd(ctx: AgentCtx, patch: Partial<WithdrawSlice>): void {
  ctx.host.setState({ wd: { ...wdOf(ctx.host.state), ...patch } });
}

type CommissionKey = 'min_comm_wd' | 'agent_wd_fee_usdt' | 'agent_wd_fee_wallet';

// 02 §3 config.commission（S-42）；缺省按 06 A-07 默认值（最低 50 USDT、usdt 手续费 1 USDT、wallet 0 USDT）
function cfgAmount(s: AgentState, key: CommissionKey, fallback: string): string {
  const v = s.sh.config?.commission[key];
  return typeof v === 'string' || typeof v === 'number' ? String(v) : fallback;
}

// P-17：TRC20 未激活地址激活服务费（config.limits.fee_trc_activation，USDT）；缺省 / 非法按 0（不提示）
function activationFee(s: AgentState): Micro {
  const v = s.sh.config?.limits['fee_trc_activation'];
  return typeof v === 'string' || typeof v === 'number' ? microOf(String(v)) : 0n;
}

// 记录行激活费说明：只在实际收过（activation_fee > 0）时显示，按 activation_state 区分已收 / 已用 / 已退
function activationText(t: AgentCtx['t'], x: WithdrawalItem): string {
  const fee = x.activation_fee === undefined ? 0n : microOf(x.activation_fee);
  if (fee <= 0n) return '';
  const key = x.activation_state === 'refunded' ? 'wdActRefunded' : x.activation_state === 'consumed' ? 'wdActConsumed' : 'wdActCharged';
  return interpolate(str(t, key), { fee: fmtAmount(fee, 2) });
}

function feeOf(s: AgentState, method: WdMethod): string {
  return method === 'usdt' ? cfgAmount(s, 'agent_wd_fee_usdt', '1') : cfgAmount(s, 'agent_wd_fee_wallet', '0');
}

// 平台钱包绑定：/me 每次切页刷新，优先于 overview（同字段，06 A-07 / R-08）
function boundUser(s: AgentState): BoundUser | null {
  return s.sh.me ? s.sh.me.wallet_bound_user : homeOf(s).overview?.wallet_bound_user ?? null;
}

// wallet 未绑定（含已选后被解绑）一律按 usdt 展示与提交：页面所见即所提
function effectiveMethod(s: AgentState): WdMethod {
  const m = wdOf(s).method;
  return m === 'wallet' && !boundUser(s) ? 'usdt' : m;
}

// 金额串非法不让整页抛错：记日志按 0 计
function microOf(v: string): Micro {
  try {
    return toMicro(v);
  } catch (e) {
    console.warn('[gp:agent] 非法金额', v, e);
    return 0n;
  }
}

// 06 A-07 状态集：6 个服务端状态 → 5 种可见文案（approved / paying 同为「出金中」，S-50）
const STATUS: Readonly<Record<WithdrawalItem['status'], readonly [string, ChipKind]>> = {
  pending: ['stReview', 'amber'],
  approved: ['stProcessing', 'blue'],
  paying: ['stProcessing', 'blue'],
  paid: ['stPaid', 'green'],
  rejected: ['stReject', 'red'],
  failed: ['stPayIssue', 'red'],
};

function shortAddr(a: string | null): string {
  if (!a) return '';
  return a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a;
}

// 在途丢弃：发请求时的会话代与写入时不一致即丢弃（跨账号不串提佣记录）
export async function loadWithdrawals(ctx: AgentCtx): Promise<void> {
  const ep = ctx.gp.session.epoch();
  try {
    const p = await ctx.gp.api.call<AdminPage<WithdrawalItem>>('GET /api/agent/withdrawals', { query: { page: 1, size: LIST_SIZE } });
    if (ctx.gp.session.epoch() !== ep) return;
    setWd(ctx, { list: p.list });
  } catch (e) {
    if (ctx.gp.session.epoch() === ep) ctx.gp.ui.toast(ctx.gp.ui.err(e));
  }
}

// 前端预检（toast，原型 as-is）：金额 → 最低提佣 → 实际到账 > 0 → 方式 / 地址；服务端同链复验（02 §5 L215）
function precheck(ctx: AgentCtx): { body: Readonly<Record<string, string>>; min: string } | string {
  const s = ctx.host.state;
  const t = ctx.t;
  const w = wdOf(s);
  const method = effectiveMethod(s);
  const available = homeOf(s).overview?.kpi.withdrawable ?? '0';
  const min = cfgAmount(s, 'min_comm_wd', '50');
  const parsed = parseDecimalInput(w.amount, 2);
  if (parsed === null) return str(t, 'errAmount');
  const amt = toMicro(parsed);
  if (cmp(amt, 0n) <= 0 || cmp(amt, microOf(available)) > 0) return str(t, 'errAmount');
  if (cmp(amt, microOf(min)) < 0) return interpolate(str(t, 'errMinWd'), { min: fmtPlain(microOf(min)) });
  if (cmp(sub(amt, microOf(feeOf(s, method))), 0n) <= 0) return str(t, 'errAmount');
  const amount = fmtAmount(amt, 2, { group: false });
  if (method === 'wallet') return { body: { amount, method: 'wallet' }, min };
  const addr = w.addr.trim();
  if (!addr) return str(t, 'errAddr');
  if (!TRON_RE.test(addr)) return str(t, 'errAddrFmt');
  return { body: { amount, method: 'usdt', usdt_address: addr }, min };
}

async function submit(ctx: AgentCtx): Promise<void> {
  const r = precheck(ctx);
  if (typeof r === 'string') return ctx.gp.ui.toast(r);
  const ep = ctx.gp.session.epoch();
  const done = await ctx.gp.ui.run(async () => {
    try {
      await ctx.gp.api.call('POST /api/agent/withdrawals', { body: r.body });
      return true;
    } catch (e) {
      // 1308 服务端复验：errMinWd 经 ui.err 同一插值
      if (ctx.gp.session.epoch() === ep) ctx.gp.ui.toast(e instanceof ApiError ? ctx.gp.ui.err(e, { min: fmtPlain(microOf(r.min)) }) : ctx.gp.ui.err(e));
      return false;
    }
  });
  if (done !== true || ctx.gp.session.epoch() !== ep) return;
  setWd(ctx, { amount: '', addr: '' });
  scheduleQuote(ctx);
  ctx.gp.ui.toast(str(ctx.t, 'toastWithdraw'));
  // 可提现佣金扣减在服务端完成：记录与 overview 各重拉一次
  await Promise.all([loadWithdrawals(ctx), loadOverview(ctx)]);
}

// ── USDT 提佣预检（2026-10-15）：金额与地址都合法后防抖调用 GET /api/agent/withdrawals/quote（服务端经 Tatum 读链判定激活）。
// 竞态：输入变化递增 seq 并中止在途请求，只认最后一次；结果带 key，与当前输入不一致不展示；跨会话按 epoch 丢弃。
const QUOTE_DEBOUNCE_MS = 500;
interface QuoteRuntime {
  timer: ReturnType<typeof setTimeout> | null;
  ac: AbortController | null;
  seq: number;
  key: string;
}
const quoteRuntimes = new WeakMap<object, QuoteRuntime>();
function qrt(host: object): QuoteRuntime {
  let r = quoteRuntimes.get(host);
  if (!r) {
    r = { timer: null, ac: null, seq: 0, key: '' };
    quoteRuntimes.set(host, r);
  }
  return r;
}

function cancelQuote(host: object): void {
  const r = qrt(host);
  r.seq++;
  if (r.timer !== null) clearTimeout(r.timer);
  r.timer = null;
  r.ac?.abort();
  r.ac = null;
  r.key = '';
}

// quoteKey：USDT 方式、激活费配置 > 0、金额合法（≥ 最低提佣且扣基础费后 > 0）、地址格式合法时返回键，否则 ''
function quoteKey(s: AgentState): string {
  if (effectiveMethod(s) !== 'usdt' || activationFee(s) <= 0n) return '';
  const w = wdOf(s);
  const addr = w.addr.trim();
  if (!TRON_RE.test(addr)) return '';
  const parsed = parseDecimalInput(w.amount, 2);
  if (parsed === null) return '';
  const amt = toMicro(parsed);
  if (cmp(amt, microOf(cfgAmount(s, 'min_comm_wd', '50'))) < 0 || cmp(sub(amt, microOf(feeOf(s, 'usdt'))), 0n) <= 0) return '';
  return `${fmtAmount(amt, 2, { group: false })}|${addr}`;
}

function currentQuote(s: AgentState): WithdrawQuote | null {
  const key = quoteKey(s);
  const q = wdOf(s).quote;
  return key !== '' && q?.key === key ? q.q : null;
}

// scheduleQuote：输入变化后调用；键未变不重复请求，键变化先清旧结果再防抖发起
function scheduleQuote(ctx: AgentCtx): void {
  const host = ctx.host;
  const key = quoteKey(host.state);
  const r = qrt(host);
  if (key === r.key) return;
  cancelQuote(host);
  if (wdOf(host.state).quote) setWd(ctx, { quote: null });
  if (key === '') return;
  r.key = key;
  const seq = r.seq;
  r.timer = setTimeout(() => void fetchQuote(ctx, key, seq), QUOTE_DEBOUNCE_MS);
}

async function fetchQuote(ctx: AgentCtx, key: string, seq: number): Promise<void> {
  const r = qrt(ctx.host);
  if (r.seq !== seq) return;
  r.timer = null;
  const [amount, address] = key.split('|');
  const ep = ctx.gp.session.epoch();
  const ac = new AbortController();
  r.ac = ac;
  try {
    const q = await ctx.gp.api.call<WithdrawQuote>('GET /api/agent/withdrawals/quote', { query: { amount, address }, signal: ac.signal });
    if (r.seq !== seq || ctx.gp.session.epoch() !== ep || quoteKey(ctx.host.state) !== key) return;
    setWd(ctx, { quote: { key, q } });
  } catch (e) {
    if (r.seq !== seq || (e instanceof DOMException && e.name === 'AbortError')) return;
    // 预检失败不打断填写：回落笼统激活费提示，权威校验仍在提交时
    console.warn('[gp:agent] 提佣预检失败，回落笼统提示', e);
    r.key = '';
  } finally {
    if (r.ac === ac) r.ac = null;
  }
}

// 预检提示金额：去尾零、不分组
function t0(v: string): string {
  const s = fmtAmount(microOf(v), 6, { group: false });
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
}

// actTip：有预检结果 → 确定结论（未激活 / 不确定 / 已激活无提示）；无结果 → 笼统预告（USDT 方式且配置了激活费）
function actTip(s: AgentState, t: AgentCtx['t'], method: WdMethod): string {
  if (method !== 'usdt' || activationFee(s) <= 0n) return '';
  const q = currentQuote(s);
  if (q === null) return interpolate(str(t, 'wdActNote'), { fee: fmtAmount(activationFee(s), 2) });
  if (q.activation_check === 'unknown') return interpolate(str(t, 'wdActUnknown'), { fee: t0(q.activation_fee) });
  if (q.needs_activation) return interpolate(str(t, 'wdActNeeded'), { activation_fee: t0(q.activation_fee), net: t0(q.net_amount) });
  return '';
}

const METHOD_STYLE = (on: boolean, enabled: boolean): string =>
  `flex:1;height:42px;border:1px solid ${on ? '#0d9a68' : '#dfe5e2'};border-radius:12px;background:${on ? '#e3f6ee' : '#fff'};color:${on ? '#0d9a68' : '#0f1a17'};font-weight:700;font-size:13px;cursor:${enabled ? 'pointer' : 'not-allowed'}${enabled ? '' : ';opacity:.5'}`;
const CHIP_STYLE = 'flex:1;height:36px;border:1px solid #dfe5e2;border-radius:10px;background:#fff;font-weight:700;font-size:12px;cursor:pointer';

export const withdraw: AgentDomain = {
  name: 'withdraw',
  keys: KEYS,
  modalKeys: MODAL_KEYS,
  migrated: true,
  i18n: I18N_WITHDRAW,
  rewrites: REWRITES_WITHDRAW,
  state: () => ({ wd: fresh() }),
  mount(ctx) {
    // 业务请求只在 screen 事件后发（S-72）；进入提佣页拉记录并刷新可提现佣金（资金类展示不沿用旧值）
    ctx.events.on('screen', ({ to }) => {
      if (to !== 'withdraw') return;
      void loadWithdrawals(ctx);
      void loadOverview(ctx);
    });
    ctx.events.on('session:lost', () => {
      cancelQuote(ctx.host);
      ctx.host.setState({ wd: fresh() });
    });
  },
  vals(ctx) {
    const { s, t } = ctx;
    const w = wdOf(s);
    const bound = boundUser(s) !== null;
    const method = effectiveMethod(s);
    const available = homeOf(s).overview?.kpi.withdrawable ?? '0';
    // 预检确定（ok）时手续费 / 实际到账以服务端为准（与建单同一计算）；否则按基础费率（激活费另行提示）
    const q = method === 'usdt' ? currentQuote(s) : null;
    const fee = q !== null && q.activation_check === 'ok' ? microOf(q.fee) : microOf(feeOf(s, method));
    const parsed = parseDecimalInput(w.amount, 2);
    const net = parsed === null ? 0n : sub(toMicro(parsed), fee);
    return {
      // 对客评审 2026-10-15 #26：佣金（可提现 / 手续费 / 到账 / 记录金额）一律「{金额} USDT」去尾零
      availableText: fmtCommission(microOf(available)),
      methods: [
        { label: str(t, 'mUsdt'), pick: () => { setWd(ctx, { method: 'usdt' }); scheduleQuote(ctx); }, style: METHOD_STYLE(method === 'usdt', true) },
        // R-08：未绑定平台用户 → 置灰不可选（服务端兜底 1307）
        { label: str(t, 'mUsd'), pick: () => { if (bound) { setWd(ctx, { method: 'wallet' }); scheduleQuote(ctx); } }, style: METHOD_STYLE(method === 'wallet', bound) },
      ],
      walletTip: bound ? '' : str(t, 'errWalletUnbound'),
      isUsdt: method === 'usdt',
      wAmount: w.amount,
      onWAmount: (e: InputEvent) => { setWd(ctx, { amount: e.target.value }); scheduleQuote(ctx); },
      wAddr: w.addr,
      onWAddr: (e: InputEvent) => { setWd(ctx, { addr: e.target.value }); scheduleQuote(ctx); },
      amountChips: ['100', '200', '500', 'MAX'].map((v) => ({
        label: v === 'MAX' ? 'MAX' : `${v} USDT`,
        pick: () => { setWd(ctx, { amount: v === 'MAX' ? fmtAmount(microOf(available), 2, { group: false }) : v }); scheduleQuote(ctx); },
        style: CHIP_STYLE,
      })),
      wFee: fmtCommission(fee),
      wNet: fmtCommission(net > 0n ? net : 0n),
      arriveText: str(t, method === 'usdt' ? 'arriveUsdt' : 'arriveUsd'),
      submitWithdraw: () => void submit(ctx),
      wdTxt: {
        inflight: str(t, 'inflightNote'),
        net: str(t, 'netAmount'),
        // 运营 UX 评审 #14 / 2026-10-15 预检：有预检结果给确定结论，无结果回落笼统预告
        act: actTip(s, t, method),
      },
      withdrawals: w.list.map((x) => {
        const [key, kind] = STATUS[x.status] ?? STATUS.pending;
        return {
          id: x.biz_no,
          amount: fmtCommission(microOf(x.amount)),
          fee: fmtCommission(microOf(x.fee)),
          net: fmtCommission(microOf(x.net_amount)),
          method: str(t, x.method === 'usdt' ? 'mUsdt' : 'mUsd'),
          addr: x.method === 'usdt' ? shortAddr(x.usdt_address) : '',
          time: fmtDateTime(x.created_at, { tz: s.sh.config?.timezone }),
          status: str(t, key),
          note: [x.status === 'failed' ? str(t, 'failedNote') : '', activationText(t, x)].filter((v) => v !== '').join(' · '),
          chip: chipStyle(kind),
        };
      }),
      withdrawEmpty: w.list.length === 0,
    };
  },
};
